import { Purchases } from '@revenuecat/purchases-capacitor';
import { applyProductGrants, buildCatalog } from '../catalog';
import { MONETIZATION } from '../config';
import { trackEvent } from '../events';
import { IapProvider, PurchaseResult, StoreProduct } from '../iap';

interface CustomerInfoLike {
  entitlements: {
    active: Record<string, unknown>;
  };
}

export class StoreIapProvider implements IapProvider {
  private configured = false;

  async initialize(): Promise<void> {
    const apiKey = MONETIZATION.ids.revenueCatApiKey;
    if (!apiKey) {
      return;
    }
    await Purchases.configure({ apiKey });
    this.configured = true;
    try {
      await Purchases.addCustomerInfoUpdateListener((info) => {
        this.applyEntitlements(info as CustomerInfoLike);
      });
    } catch {
      return;
    }
    await this.syncEntitlements();
  }

  private applyEntitlements(info: CustomerInfoLike): void {
    applyProductGrants(Object.keys(info.entitlements.active));
  }

  private async syncEntitlements(): Promise<void> {
    try {
      const { customerInfo } = await Purchases.getCustomerInfo();
      this.applyEntitlements(customerInfo as CustomerInfoLike);
    } catch {
      return;
    }
  }

  async getProducts(): Promise<StoreProduct[]> {
    if (!this.configured) {
      return [];
    }
    const catalog = buildCatalog();
    const { products } = await Purchases.getProducts({
      productIdentifiers: catalog.map((entry) => entry.id),
    });
    return products.map((product) => ({
      id: product.identifier,
      title: product.title,
      description: product.description,
      priceLabel: product.priceString,
      kind: 'non-consumable' as const,
    }));
  }

  async purchase(productId: string): Promise<PurchaseResult> {
    if (!this.configured) {
      return 'unavailable';
    }
    trackEvent('skin_purchase_started', { productId, provider: 'store' });
    try {
      const { products } = await Purchases.getProducts({ productIdentifiers: [productId] });
      const product = products[0];
      if (!product) {
        trackEvent('purchase_failed', { productId, provider: 'store', reason: 'unknown-product' });
        return 'unavailable';
      }
      const result = await Purchases.purchaseStoreProduct({ product });
      this.applyEntitlements(result.customerInfo as CustomerInfoLike);
      trackEvent('purchase_completed', { productId, provider: 'store' });
      return 'purchased';
    } catch (error) {
      const failure = error as { userCancelled?: boolean };
      if (failure?.userCancelled) {
        trackEvent('purchase_cancelled', { productId, provider: 'store' });
        return 'cancelled';
      }
      trackEvent('purchase_failed', { productId, provider: 'store' });
      return 'failed';
    }
  }

  async restore(): Promise<string[]> {
    if (!this.configured) {
      return [];
    }
    try {
      const { customerInfo } = await Purchases.restorePurchases();
      const info = customerInfo as CustomerInfoLike;
      const active = Object.keys(info.entitlements.active);
      this.applyEntitlements(info);
      trackEvent('purchases_restored', { provider: 'store', count: active.length });
      return active;
    } catch {
      return [];
    }
  }
}
